import { useEffect, useMemo, useState } from 'react';

import { faGear } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faBullseyeArrow } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import { isToday } from 'date-fns';
import { useAtomValue } from 'jotai';

import { useGetDailyGoals } from '../../../../react-query/useDailyGoals';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { selectDateAtom, selectedProvidersAtom } from '../../atoms';
import { ViewAllButton } from '../ViewAllButton';

import { DailyGoalRow } from './DailyGoalRow';

interface DailyGoalsProps {
  loading?: boolean;
}

const LOW_HEIGHT_THRESHOLD = 800;

export const DailyGoals = ({ loading = false }: DailyGoalsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLowHeight, setIsLowHeight] = useState(false);
  const selectedDate = useAtomValue(selectDateAtom);
  const selectedProviders = useAtomValue(selectedProvidersAtom);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();

  // Get provider IDs for filtering
  const providerIds = useMemo(() => {
    const ids = selectedProviders
      .map((p) => p.id)
      .filter((id): id is string => id !== undefined);
    return ids.length > 0 ? ids : undefined;
  }, [selectedProviders]);

  // Fetch daily goals data
  const { filteredAndSortedGoals, result } = useGetDailyGoals({
    date: selectedDate,
    providerIds,
  });

  const isLoadingGoals = result.isLoading || loading;

  // Handle window resize for low-height behavior
  useEffect(() => {
    const handleResize = () => {
      setIsLowHeight(window.innerHeight < LOW_HEIGHT_THRESHOLD);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChangeExpanded = (newIsExpanded: boolean) => {
    setIsExpanded(newIsExpanded);
    trackEvent(mixpanelEvents.userViewAllClick, {
      expanded: newIsExpanded ? 'all' : 'less',
      context: 'DAILY_GOALS',
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
  };

  const baseLimit = isLowHeight ? 2 : 5;
  const canExpand = filteredAndSortedGoals.length > baseLimit;

  // Determine how many items to show
  const displayLimit = isExpanded ? filteredAndSortedGoals.length : baseLimit;
  const goalsToShow = filteredAndSortedGoals.slice(0, displayLimit);

  if (!selectedDate || filteredAndSortedGoals.length === 0) {
    return null;
  }

  return (
    <>
      <div className="vai-flex vai-flex-col vai-gap-2 vai-mt-4">
        <div className="vai-flex vai-items-center vai-justify-between vai-px-2">
          <div className="vai-flex vai-items-center vai-gap-2">
            <FontAwesomeIcon
              className="vai-text-zinc-600"
              icon={faBullseyeArrow}
            />
            <h3 className="vai-font-inter vai-text-[14px] vai-font-semibold vai-leading-[16px] vai-text-zinc-600">
              {t`Daily Goals`}
            </h3>
          </div>
          {/* TODO: Add onClick handler to open modal for setting/creating daily goals */}
          <button
            className="vai-cursor-pointer vai-bg-transparent vai-border-none hover:vai-opacity-80"
            type="button"
          >
            <FontAwesomeIcon
              icon={faGear}
              className="vai-h-4 vai-w-4 vai-text-muted-foreground"
            />
          </button>
        </div>
        <div className="vai-flex vai-flex-col">
          {goalsToShow.map((goal) => (
            <DailyGoalRow
              key={`${goal.providerId}-${goal.date}`}
              completedProduction={goal.completedProduction}
              goal={goal.goal}
              providerName={goal.providerName}
              scheduledProduction={goal.scheduledProduction}
            />
          ))}
        </div>

        {canExpand && !isLoadingGoals && (
          <ViewAllButton
            dataTestId="view-all-daily-goals"
            expanded={isExpanded}
            onChange={handleChangeExpanded}
          />
        )}
      </div>
    </>
  );
};
